import { PDFDocument, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  pdfToDocxEnhanced, 
  pdfToExcelEnhanced, 
  pdfToPowerPointEnhanced,
  pdfToWordFormatted,
  pdfToExcelWithTables
} from './msOfficeUtils';

// Debug: Verify imports
console.log('Imported functions:', {
  pdfToDocxEnhanced: typeof pdfToDocxEnhanced,
  pdfToExcelEnhanced: typeof pdfToExcelEnhanced,
  pdfToPowerPointEnhanced: typeof pdfToPowerPointEnhanced,
  pdfToWordFormatted: typeof pdfToWordFormatted,
  pdfToExcelWithTables: typeof pdfToExcelWithTables
});

// Helper function to create blob URL
const createBlobUrl = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  return { url, filename };
};

// Enhanced PDF to DOCX conversion
export const pdfToDocx = async (file, onProgress) => {
  return await pdfToDocxEnhanced(file, onProgress);
};

// PDF to TXT conversion
export const pdfToTxt = async (file, onProgress) => {
  onProgress(20);
  
  // Simplified text extraction
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(60);
  
  let extractedText = '';
  const pages = pdfDoc.getPages();
  
  for (let i = 0; i < pages.length; i++) {
    extractedText += `Page ${i + 1}:\n`;
    extractedText += `[Text content would be extracted here]\n\n`;
    onProgress(60 + (i / pages.length) * 30);
  }
  
  onProgress(95);
  
  const blob = new Blob([extractedText], { type: 'text/plain' });
  const filename = file.name.replace('.pdf', '.txt');
  
  onProgress(100);
  
  return createBlobUrl(blob, filename);
};

// PDF to Image conversion
export const pdfToImage = async (file, format = 'jpg', onProgress) => {
  onProgress(10);
  
  // This is a simplified implementation
  // In a real application, you'd use pdf2pic or similar library
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  
  onProgress(30);
  
  const pages = pdfDoc.getPages();
  const images = [];
  
  for (let i = 0; i < pages.length; i++) {
    // Create a canvas representation of the page
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (simplified)
    canvas.width = 800;
    canvas.height = 600;
    
    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add text indicating this is a placeholder
    ctx.fillStyle = 'black';
    ctx.font = '16px Arial';
    ctx.fillText(`Page ${i + 1} of ${pages.length}`, 50, 50);
    ctx.fillText('PDF to Image conversion', 50, 80);
    ctx.fillText('(This is a placeholder)', 50, 110);
    
    const imageDataUrl = canvas.toDataURL(`image/${format}`);
    images.push(imageDataUrl);
    
    onProgress(30 + (i / pages.length) * 60);
  }
  
  onProgress(95);
  
  // For multiple pages, we'll create a ZIP file or return the first image
  if (images.length === 1) {
    const response = await fetch(images[0]);
    const blob = await response.blob();
    const filename = file.name.replace('.pdf', `.${format}`);
    
    onProgress(100);
    return createBlobUrl(blob, filename);
  } else {
    // For multiple images, create a simple text file listing them
    const imageList = images.map((img, index) => `Page ${index + 1}: ${img}`).join('\n');
    const blob = new Blob([imageList], { type: 'text/plain' });
    const filename = file.name.replace('.pdf', '_images.txt');
    
    onProgress(100);
    return createBlobUrl(blob, filename);
  }
};


// DOCX to PDF conversion
export const docxToPdf = async (file, onProgress) => {
  onProgress(10);

  try {
    // Convert DOCX to HTML using mammoth
    const arrayBuffer = await file.arrayBuffer();
    onProgress(30);
    
    const result = await mammoth.convertToHtml({ arrayBuffer });
    onProgress(50);
    
    // Create temporary div to render HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = result.value;
    tempDiv.style.width = '210mm';
    tempDiv.style.padding = '20px';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.fontSize = '12px';
    tempDiv.style.lineHeight = '1.5';
    
    document.body.appendChild(tempDiv);
    onProgress(70);
    
    // Convert HTML to canvas
    const canvas = await html2canvas(tempDiv, { 
      scale: 2,
      useCORS: true,
      allowTaint: true 
    });
    document.body.removeChild(tempDiv);
    onProgress(85);
    
    // Create PDF from canvas
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    
    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    onProgress(95);
    
    const pdfBlob = pdf.output('blob');
    const filename = file.name.replace(/\.(docx|doc)$/i, '.pdf');
    
    onProgress(100);
    return createBlobUrl(pdfBlob, filename);
    
  } catch (error) {
    throw new Error(`DOCX to PDF conversion failed: ${error.message}`);
  }
};

// DOCX to TXT conversion
export const docxToTxt = async (file, onProgress) => {
  onProgress(20);
  
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  
  onProgress(80);
  
  const blob = new Blob([result.value], { type: 'text/plain' });
  const filename = file.name.replace(/\.(docx|doc)$/i, '.txt');
  
  onProgress(100);
  
  return createBlobUrl(blob, filename);
};

// XLSX to PDF conversion
export const xlsxToPdf = async (file, onProgress) => {
  onProgress(10);
  
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  
  onProgress(30);
  
  // Convert first sheet to HTML
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const html = XLSX.utils.sheet_to_html(worksheet);
  
  onProgress(50);
  
  // Create temporary div
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  tempDiv.style.width = '210mm';
  tempDiv.style.padding = '20px';
  tempDiv.style.fontFamily = 'Arial, sans-serif';
  tempDiv.style.fontSize = '10px';
  
  document.body.appendChild(tempDiv);
  
  onProgress(70);
  
  const canvas = await html2canvas(tempDiv, { scale: 2 });
  document.body.removeChild(tempDiv);
  
  onProgress(85);
  
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  const imgWidth = 210;
  const pageHeight = 295;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
  pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
  
  onProgress(95);
  
  const pdfBlob = pdf.output('blob');
  const filename = file.name.replace(/\.(xlsx|xls)$/i, '.pdf');
  
  onProgress(100);
  
  return createBlobUrl(pdfBlob, filename);
};

// XLSX to CSV conversion
export const xlsxToCsv = async (file, onProgress) => {
  onProgress(20);
  
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  
  onProgress(60);
  
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  
  onProgress(90);
  
  const blob = new Blob([csv], { type: 'text/csv' });
  const filename = file.name.replace(/\.(xlsx|xls)$/i, '.csv');
  
  onProgress(100);
  
  return createBlobUrl(blob, filename);
};

// CSV to XLSX conversion
export const csvToXlsx = async (file, onProgress) => {
  onProgress(20);
  
  const text = await file.text();
  const workbook = XLSX.utils.book_new();
  
  onProgress(40);
  
  const worksheet = XLSX.utils.aoa_to_sheet(
    text.split('\n').map(row => row.split(','))
  );
  
  onProgress(70);
  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
  onProgress(90);
  
  const blob = new Blob([xlsxBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  const filename = file.name.replace('.csv', '.xlsx');
  
  onProgress(100);
  
  return createBlobUrl(blob, filename);
};

// Image to PDF conversion
export const imageToPdf = async (file, onProgress) => {
  onProgress(20);
  
  const img = new Image();
  const imgUrl = URL.createObjectURL(file);
  
  return new Promise((resolve, reject) => {
    img.onload = () => {
      onProgress(50);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (img.height * imgWidth) / img.width;
      
      pdf.addImage(imgUrl, 'JPEG', 0, 0, imgWidth, imgHeight);
      
      onProgress(90);
      
      const pdfBlob = pdf.output('blob');
      const filename = file.name.replace(/\.(jpg|jpeg|png|tiff|tif)$/i, '.pdf');
      
      URL.revokeObjectURL(imgUrl);
      
      onProgress(100);
      resolve(createBlobUrl(pdfBlob, filename));
    };
    
    img.onerror = reject;
    img.src = imgUrl;
  });
};

// Main conversion function
export const convertDocument = async (file, conversionType, onProgress) => {
  onProgress(0);
  
  try {
    switch (conversionType) {
      case 'pdf-to-docx':
        return await pdfToDocx(file, onProgress);
      case 'pdf-to-docx-formatted':
        return await pdfToWordFormatted(file, onProgress);
      case 'pdf-to-xlsx':
        return await pdfToExcelEnhanced(file, onProgress);
      case 'pdf-to-xlsx-tables':
        return await pdfToExcelWithTables(file, onProgress);
      case 'pdf-to-pptx':
        return await pdfToPowerPointEnhanced(file, onProgress);
      case 'pdf-to-txt':
        return await pdfToTxt(file, onProgress);
      case 'pdf-to-jpg':
        return await pdfToImage(file, 'jpg', onProgress);
      case 'pdf-to-png':
        return await pdfToImage(file, 'png', onProgress);
      case 'docx-to-pdf':
        return await docxToPdf(file, onProgress);
      case 'docx-to-txt':
        return await docxToTxt(file, onProgress);
      case 'docx-to-jpg':
        // Convert via PDF first
        const pdfResult = await docxToPdf(file, (p) => onProgress(p * 0.5));
        return await pdfToImage(pdfResult, 'jpg', (p) => onProgress(50 + p * 0.5));
      case 'xlsx-to-pdf':
        return await xlsxToPdf(file, onProgress);
      case 'xlsx-to-csv':
        return await xlsxToCsv(file, onProgress);
      case 'csv-to-xlsx':
        return await csvToXlsx(file, onProgress);
      case 'pptx-to-pdf':
        // Placeholder - would need PowerPoint processing library
        throw new Error('PowerPoint to PDF conversion not yet implemented');
      case 'pptx-to-jpg':
        // Placeholder
        throw new Error('PowerPoint to image conversion not yet implemented');
      case 'html-to-pdf':
        // Placeholder
        throw new Error('HTML to PDF conversion not yet implemented');
      case 'md-to-pdf':
        // Placeholder
        throw new Error('Markdown to PDF conversion not yet implemented');
      case 'image-to-pdf':
        return await imageToPdf(file, onProgress);
      default:
        throw new Error(`Unsupported conversion type: ${conversionType}`);
    }
  } catch (error) {
    console.error('Conversion error:', error);
    throw new Error(`Conversion failed: ${error.message}`);
  }
};
